import React, { memo, useState, useEffect } from 'react';
import { validators } from 'investira.sdk';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Button,
  LoadingButton
} from 'investira.react.components';
import { useEntidadesContext } from '../../contexts/EntidadesContext';
import withMessage from '../../hoc/withMessage';
import { ASSINATURA_TIPO } from '../../enums';
import useAssinatura from '../../hooks/useAssinatura';

const xPapeisAssinatura = [
  ASSINATURA_TIPO.RG.descricao,
  ASSINATURA_TIPO.CN.descricao,
  ASSINATURA_TIPO.VR.descricao,
  ASSINATURA_TIPO.AP.descricao
];
const xPapelObrigatorio = ASSINATURA_TIPO.RG.descricao;
const xAssinaturaTipoPorDescricao = {
  [ASSINATURA_TIPO.RG.descricao]: ASSINATURA_TIPO.RG,
  [ASSINATURA_TIPO.CN.descricao]: ASSINATURA_TIPO.CN,
  [ASSINATURA_TIPO.VR.descricao]: ASSINATURA_TIPO.VR,
  [ASSINATURA_TIPO.AP.descricao]: ASSINATURA_TIPO.AP
};
const xAssinaturaFlags = [ASSINATURA_TIPO.CN, ASSINATURA_TIPO.VR, ASSINATURA_TIPO.AP];
const xAssinaturaFallback = ASSINATURA_TIPO.RG.valor;

const xDefaultAssinaturas = {
  RLENQ: [xPapelObrigatorio], // Papéis de Assinatura
  RLVAR: [xPapelObrigatorio],
  RLDUR: [xPapelObrigatorio],
  RLSTT: [xPapelObrigatorio],
  RLBCK: [xPapelObrigatorio],
  RLCRL: [xPapelObrigatorio],
  RLFEF: [xPapelObrigatorio],
  RLCNC: [xPapelObrigatorio],
  CFENQ: [xPapelObrigatorio]
};
const xAssinaturasKeys = Object.keys(xDefaultAssinaturas);

const isSameAssinaturasState = (pPrev, pNext) => {
  return xAssinaturasKeys.every(xKey => {
    const xPrev = pPrev?.[xKey] || [xPapelObrigatorio];
    const xNext = pNext?.[xKey] || [xPapelObrigatorio];

    return xPrev.length === xNext.length && xPrev.every((xItem, xIndex) => xItem === xNext[xIndex]);
  });
};

const normalizePapeis = (pValue, pPapeisDisponiveis = xPapeisAssinatura) => {
  const xValue = Array.isArray(pValue) ? pValue : pValue ? [pValue] : [];
  const xSemDuplicado = [...new Set([xPapelObrigatorio, ...xValue])];

  return xSemDuplicado.filter(xPapel => pPapeisDisponiveis.includes(xPapel));
};

const normalizeAssinaturaValue = pValue => {
  if (Number.isFinite(pValue) && pValue >= 0) {
    return pValue;
  }

  if (typeof pValue === 'string') {
    const xParsedValue = Number(pValue.trim());

    if (pValue.trim() !== '' && Number.isFinite(xParsedValue) && xParsedValue >= 0) {
      return xParsedValue;
    }
  }

  return xAssinaturaFallback;
};

const decodeAssinaturaValue = (pValue, pPapeisDisponiveis = xPapeisAssinatura) => {
  const xValor = normalizeAssinaturaValue(pValue);
  const xPapeis = [xPapelObrigatorio];

  xAssinaturaFlags.forEach(xTipo => {
    if ((xValor & xTipo.valor) === xTipo.valor) {
      xPapeis.push(xTipo.descricao);
    }
  });

  return normalizePapeis(xPapeis, pPapeisDisponiveis);
};

const encodeAssinaturaValue = (pPapeis, pPapeisDisponiveis = xPapeisAssinatura) => {
  const xPapeisNormalizados = normalizePapeis(pPapeis, pPapeisDisponiveis);

  return xPapeisNormalizados.reduce((xAcc, xPapel) => {
    const xTipo = xAssinaturaTipoPorDescricao[xPapel];

    return xTipo ? xAcc | xTipo.valor : xAcc;
  }, ASSINATURA_TIPO.RG.valor);
};

const MembroAssinaturas = memo(props => {
  const { user = {}, open, recursosLabels, onClose } = props;
  const { actions } = useEntidadesContext();
  const xRecursosLabels = recursosLabels || {};
  const xRecursosKeys = Object.keys(xRecursosLabels);
  const { tiposAssinaturaDisponiveis: xPapeisAssinaturaDisponiveis } = useAssinatura();

  // Inicializa acessos vindo de user.assinaturas, se houver, ou com o padrão
  const [assinaturas, setAssinaturas] = useState(xDefaultAssinaturas);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const xNextAssinaturas = open
      ? xAssinaturasKeys.reduce((xAcc, xKey) => {
          const xAssinaturasUsuario = user?.assinaturas || {};
          xAcc[xKey] = decodeAssinaturaValue(
            xAssinaturasUsuario[xKey],
            xPapeisAssinaturaDisponiveis
          );
          return xAcc;
        }, {})
      : xAssinaturasKeys.reduce((xAcc, xKey) => {
          xAcc[xKey] = [xPapelObrigatorio];
          return xAcc;
        }, {});

    setAssinaturas(xPrev =>
      isSameAssinaturasState(xPrev, xNextAssinaturas) ? xPrev : xNextAssinaturas
    );
  }, [open, user?.usuario_id, user?.assinaturas, xPapeisAssinaturaDisponiveis]);

  const handleChangeAcesso = pKey => pEvent => {
    setAssinaturas(xPrev => ({
      ...xPrev,
      [pKey]: normalizePapeis(pEvent.target.value, xPapeisAssinaturaDisponiveis)
    }));
  };

  const handleSave = () => {
    const xPayloadAssinaturas = xAssinaturasKeys.reduce((xAcc, xKey) => {
      xAcc[xKey] = encodeAssinaturaValue(assinaturas[xKey], xPapeisAssinaturaDisponiveis);
      return xAcc;
    }, {});

    setIsSubmitting(true);
    actions.handleModifyMembro(
      {
        usuario_id: user.usuario_id,
        assinaturas: xPayloadAssinaturas
      },
      rRes => {
        props.onMessageSuccess('Assinaturas modificadas com sucesso.');
        actions.readMembros && actions.readMembros();
        onClose && onClose();
      },
      rErr => {
        props.onMessageError('Erro ao modificar assinaturas.');
      },
      () => {
        setIsSubmitting(false);
      }
    );
  };

  if (validators.isEmpty(user)) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Definir Níveis de Assinatura</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {xRecursosKeys.map(xKey => {
            const { acessos = {} } = user;

            if (acessos[xKey] !== 'A') {
              return null;
            }

            const xPapeisSelecionados = normalizePapeis(
              assinaturas[xKey] || [xPapelObrigatorio],
              xPapeisAssinaturaDisponiveis
            );

            return (
              <FormControl fullWidth key={xKey} size="small" variant="outlined">
                <InputLabel id={`label-select-${xKey}`} shrink>
                  {xRecursosLabels[xKey]}
                </InputLabel>
                <Select
                  labelId={`label-select-${xKey}`}
                  label={xRecursosLabels[xKey]}
                  value={xPapeisSelecionados}
                  onChange={handleChangeAcesso(xKey)}
                  multiple
                  renderValue={xSelected => xSelected.join(', ')}>
                  {xPapeisAssinaturaDisponiveis.map(xPapel => (
                    <MenuItem key={xPapel} value={xPapel} disabled={xPapel === xPapelObrigatorio}>
                      <Checkbox checked={xPapeisSelecionados.includes(xPapel)} />
                      <ListItemText primary={xPapel} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          size="small"
          sx={{ px: 2 }}
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={handleSave}>
          Salvar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default withMessage(MembroAssinaturas);
